'use client';
import { useTranslation } from '@root/src/i18n/client';
import Image from '@components/common/ImageFallback';

interface Content {
  id: string | number;
  title: string;
  slug: string;
  description?: string;
  image?: string[] | string;
  createdAt?: string;
  publishedAt?: string;
  author?: string;
}

interface Topic {
  id: string | number;
  name: string;
  slug: string;
}

interface ContentsListProps {
  topic: Topic;
  contents: Content[];
  onSelectContent: (content: Content) => void;
  onBack: () => void;
}

export default function ContentsList({
  topic,
  contents,
  onSelectContent,
  onBack,
}: ContentsListProps) {
  const { t } = useTranslation('common');

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getImageUrl = (image: string[] | string | undefined) => {
    if (!image) return null;
    if (Array.isArray(image)) return image[0];
    return image;
  };
  const getDisplayDate = (content: Content) => content.publishedAt || content.createdAt;

  if (!contents || contents.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#666]">{t('common.noData', 'Không có bài viết')}</p>
        <button
          onClick={onBack}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#223A27] px-6 py-2 text-sm font-semibold text-white hover:bg-[#1a2b1f] transition-colors"
        >
          ← {t('common.back', 'Quay lại')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 pb-6 border-b border-[#EDE6D8]">
        <button
          onClick={onBack}
          className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#7F6B48] hover:text-[#223A27] transition-colors"
        >
          ← {t('common.back', 'Quay lại')}
        </button>
        <h2 className="text-3xl md:text-4xl font-bold text-[#223A27]">{topic.name}</h2>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contents.map((content) => {
          const imageUrl = getImageUrl(content.image);
          return (
            <article
              key={content.id}
              className="group overflow-hidden rounded-[24px] bg-white shadow-sm hover:shadow-lg transition-shadow duration-300 cursor-pointer"
              onClick={() => onSelectContent(content)}
            >
              {/* Image */}
              <div className="relative h-48 bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8] overflow-hidden">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={content.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-5xl text-[#223A27]/20">
                    📄
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col gap-4">
                {/* Meta */}
                <div className="flex items-center justify-between gap-2">
                  {getDisplayDate(content) && (
                    <span className="text-xs text-[#7F6B48] font-medium">
                      📅 {formatDate(getDisplayDate(content) || '')}
                    </span>
                  )}
                  {content.author && (
                    <span className="text-xs text-[#7F6B48] font-medium">
                      ✍️ {content.author}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-[#223A27] group-hover:text-[#1a2b1f] transition-colors line-clamp-2">
                  {content.title}
                </h3>

                {/* Description */}
                {content.description && (
                  <p className="text-sm text-[#666] leading-6 line-clamp-3">
                    {content.description}
                  </p>
                )}

                {/* Read More */}
                <div className="mt-auto pt-4 border-t border-[#E8DCC8]">
                  <button className="text-sm font-semibold text-[#223A27] hover:text-[#1a2b1f] transition-colors flex items-center gap-1">
                    {t('lytra.expReadMore', 'Đọc thêm')}
                    <span className="text-lg">→</span>
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
